import * as React from 'react';
import cn from 'classnames';

import type {
  NewsContent,
  NewsContentMark,
  NewsContentNode
} from 'shared/entities/newsContent';
import {
  getImageAlignment,
  getImageDimension,
  getStringAttribute,
  getTextAlignment,
  isSafeLink
} from 'shared/entities/newsContent';
import Badge from 'shared/newComponents/Badge';

import './NewsContentBody.modules.scss';

type Props = {
  className?: string;
  content: NewsContent;
};

const renderMark = (
  mark: NewsContentMark,
  children: React.ReactNode
): React.ReactNode => {
  switch (mark.type) {
    case 'bold':
      return <strong>{children}</strong>;
    case 'italic':
      return <em>{children}</em>;
    case 'underline':
      return <u>{children}</u>;
    case 'strike':
      return <s>{children}</s>;
    case 'link': {
      const href = getStringAttribute(mark.attrs, 'href');

      return href && isSafeLink(href) ? (
        <a href={href} rel="noreferrer" target="_blank">
          {children}
        </a>
      ) : (
        children
      );
    }
    default:
      return children;
  }
};

const renderTextNode = (node: NewsContentNode): React.ReactNode =>
  (node.marks || []).reduce<React.ReactNode>(
    (children, mark) => renderMark(mark, children),
    node.text || ''
  );

const renderNodeChildren = (
  nodes: NewsContentNode[] | undefined
): React.ReactNode =>
  nodes?.map((node, index) => (
    <React.Fragment key={`${node.type}-${index}`}>
      {renderNewsContentNode(node)}
    </React.Fragment>
  ));

const renderNewsContentNode = (node: NewsContentNode): React.ReactNode => {
  const children = renderNodeChildren(node.content);

  switch (node.type) {
    case 'text':
      return renderTextNode(node);
    case 'paragraph':
      return (
        <p
          styleName={cn(
            'news-content-body__paragraph',
            `news-content-body__text-align_${getTextAlignment(node.attrs)}`
          )}
        >
          {node.content?.length ? children : '\u00a0'}
        </p>
      );
    case 'heading': {
      const level = node.attrs?.level;
      const textAlignmentClassName = `news-content-body__text-align_${getTextAlignment(
        node.attrs
      )}`;

      if (level === 2) {
        return (
          <h2
            styleName={cn('news-content-body__heading', textAlignmentClassName)}
          >
            {children}
          </h2>
        );
      }

      if (level === 3) {
        return (
          <h3
            styleName={cn('news-content-body__heading', textAlignmentClassName)}
          >
            {children}
          </h3>
        );
      }

      if (level === 4) {
        return (
          <h4
            styleName={cn('news-content-body__heading', textAlignmentClassName)}
          >
            {children}
          </h4>
        );
      }

      return (
        <p
          styleName={cn('news-content-body__paragraph', textAlignmentClassName)}
        >
          {children}
        </p>
      );
    }
    case 'bulletList':
      return <ul styleName="news-content-body__list">{children}</ul>;
    case 'orderedList':
      return <ol styleName="news-content-body__list">{children}</ol>;
    case 'listItem':
      return <li>{children}</li>;
    case 'hardBreak':
      return <br />;
    case 'image': {
      const src = getStringAttribute(node.attrs, 'src');
      const width = getImageDimension(node.attrs, 'width');
      const height = getImageDimension(node.attrs, 'height');
      const imageAlignment = getImageAlignment(node.attrs);

      return src ? (
        <img
          alt={getStringAttribute(node.attrs, 'alt') || ''}
          src={src}
          style={{ height, width }}
          styleName={cn(
            'news-content-body__image',
            `news-content-body__image_${imageAlignment}`
          )}
        />
      ) : null;
    }
    case 'doc':
      return children;
    default:
      return children;
  }
};

const NewsContentBody: React.FC<Props> = ({ className, content }) => (
  <article className={className} styleName="news-content-body">
    <div styleName="news-content-body__body">
      {content.badgeLabel && (
        <Badge styleName="news-content-body__badge">{content.badgeLabel}</Badge>
      )}
      {renderNewsContentNode(content.body)}
    </div>
  </article>
);

export default NewsContentBody;
